import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Playground } from '@/types/playground';
import * as api from '@/lib/api';

interface PlaygroundsState {
  items: Playground[];
  selected: Playground | null;
  isLoading: boolean;
  error: string | null;
  loadedCount: number;
  totalCount: number;
}

const initialState: PlaygroundsState = {
  items: [],
  selected: null,
  isLoading: false,
  error: null,
  loadedCount: 0,
  totalCount: 0,
};

// Загрузка ограниченного количества площадок для главной страницы
export const fetchPlaygroundsLimited = createAsyncThunk(
  'playgrounds/fetchLimited',
  async (limit: number = 6, { rejectWithValue, getState }) => {
    try {
      const { getPlaygroundsLimited } = await import('@/lib/api');
      const playgrounds = await getPlaygroundsLimited(limit);
      
      // Маппим центры к площадкам
      const state = getState() as any;
      let centersData = state.centers?.items || [];
      
      if (centersData.length === 0) {
        console.log('🔄 Центры не загружены, загружаем...');
        const { getCenters } = await import('@/lib/api');
        try {
          centersData = await getCenters();
          console.log('✅ Загружено центров:', centersData.length);
        } catch (centerError) {
          console.error('❌ Ошибка загрузки центров:', centerError);
          centersData = [];
        }
      }
      
      const { getSportsList } = await import('@/lib/sports');
      
      const mappedPlaygrounds = playgrounds.map((playground: any) => {
        const center = centersData.find((c: any) => c.id === playground.complexId);
        
        let sportCodes: number | number[] | undefined;
        if (playground.sport) {
          sportCodes = playground.sport;
        } else if (playground.sports) {
          if (typeof playground.sports === 'object' && !Array.isArray(playground.sports)) {
            sportCodes = Object.values(playground.sports).flat() as number[];
          } else if (Array.isArray(playground.sports)) {
            sportCodes = playground.sports;
          }
        }
        
        const mappedSports = getSportsList(sportCodes).map((sport) => ({
          id: sport.code,
          name: sport.russianName,
          code: sport.code,
          russianName: sport.russianName,
        }));
        
        return {
          ...playground,
          center: center || (playground.complexId ? { id: playground.complexId } : undefined),
          city: playground.city || center?.city,
          address: playground.address || center?.address,
          sports: mappedSports.length > 0 ? mappedSports : (playground.sport ? [{ id: playground.sport, name: `Спорт ${playground.sport}`, code: playground.sport }] : []),
        };
      });
      
      return mappedPlaygrounds;
    } catch (error: any) {
      const errorMessage = error.response?.data || error.message || String(error);
      console.error('❌ Ошибка в fetchPlaygroundsLimited:', errorMessage);
      if (errorMessage.includes('401') || errorMessage.includes('CORS') || errorMessage.includes('Network Error')) {
        return [];
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchPlaygrounds = createAsyncThunk(
  'playgrounds/fetchAll',
  async (_, { rejectWithValue, getState }) => {
    try {
      const playgrounds = await api.getPlaygrounds();
      
      // Маппим центры к площадкам (как в мобильном приложении)
      const state = getState() as any;
      let centersData = state.centers?.items || [];
      
      // Если центры еще не загружены, загружаем их
      if (centersData.length === 0) {
        console.log('🔄 Центры не загружены, загружаем...');
        const { getCenters } = await import('@/lib/api');
        try {
          centersData = await getCenters();
          console.log('✅ Загружено центров:', centersData.length);
        } catch (centerError) {
          console.error('❌ Ошибка загрузки центров:', centerError);
          centersData = [];
        }
      }
      
      console.log('🏙️ Маппинг центров к площадкам:', {
        playgroundsCount: playgrounds.length,
        centersCount: centersData.length,
      });
      
      // Маппим виды спорта используя битовые флаги
      const { getSportsList } = await import('@/lib/sports');
      
      // Маппим центры к площадкам
      const mappedPlaygrounds = playgrounds.map((playground: any) => {
        const center = centersData.find((c: any) => c.id === playground.complexId);
        
        // Маппим виды спорта
        let sportCodes: number | number[] | undefined;
        if (playground.sport) {
          sportCodes = playground.sport;
        } else if (playground.sports) {
          // Если это объект с ключами WorkingHoursType, берем все значения
          if (typeof playground.sports === 'object' && !Array.isArray(playground.sports)) {
            sportCodes = Object.values(playground.sports).flat() as number[];
          } else if (Array.isArray(playground.sports)) {
            sportCodes = playground.sports;
          }
        }
        
        const mappedSports = getSportsList(sportCodes).map((sport) => ({
          id: sport.code,
          name: sport.russianName,
          code: sport.code,
          russianName: sport.russianName,
        }));
        
        return {
          ...playground,
          center: center || (playground.complexId ? { id: playground.complexId } : undefined),
          // Добавляем город и адрес из центра, если их нет у площадки
          city: playground.city || center?.city,
          address: playground.address || center?.address,
          // Маппим виды спорта
          sports: mappedSports.length > 0 ? mappedSports : (playground.sport ? [{ id: playground.sport, name: `Спорт ${playground.sport}`, code: playground.sport }] : []),
        };
      });
      
      console.log('✅ Маппинг завершен. Примеры:', mappedPlaygrounds.slice(0, 3).map((p: any) => ({
        id: p.id,
        name: p.name,
        city: p.city,
        centerCity: p.center?.city,
        address: p.address,
        centerAddress: p.center?.address,
      })));
      
      return mappedPlaygrounds;
    } catch (error: any) {
      // Если getPlaygrounds вернул пустой массив (из-за ошибки авторизации или CORS),
      // не устанавливаем состояние ошибки, так как это нормально для публичного доступа
      const errorMessage = error.response?.data || error.message || String(error);
      console.error('❌ Ошибка в fetchPlaygrounds:', errorMessage);
      
      // Если это ошибка авторизации или CORS, возвращаем пустой массив вместо ошибки
      // (api.getPlaygrounds уже обработал эти случаи и вернул пустой массив)
      if (errorMessage.includes('401') || errorMessage.includes('CORS') || errorMessage.includes('Network Error')) {
        console.log('⚠️ Ошибка авторизации/CORS, возвращаем пустой массив');
        return [];
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchPlayground = createAsyncThunk(
  'playgrounds/fetchOne',
  async (id: number | string, { getState }) => {
    const playground = await api.getPlayground(id);
    
    // Логируем что пришло с API
    console.log('📦 Playground data from API:', playground);
    
    // Маппим данные в нужный формат
    const state = getState() as any;
    const centers = state.centers?.items || [];
    
    // Находим центр
    const center = centers.find((c: any) => c.id === playground.complexId);
    
    // Маппим виды спорта используя битовые флаги
    const { getSportsList } = await import('@/lib/sports');
    let sportCodes: number | number[] | undefined;
    
    // Определяем откуда брать коды видов спорта
    if (playground.sport) {
      sportCodes = playground.sport;
    } else if (playground.sports) {
      // Если это объект с ключами WorkingHoursType, берем все значения
      if (typeof playground.sports === 'object' && !Array.isArray(playground.sports)) {
        sportCodes = Object.values(playground.sports).flat() as number[];
      } else if (Array.isArray(playground.sports)) {
        sportCodes = playground.sports;
      }
    }
    
    const mappedSports = getSportsList(sportCodes).map((sport) => ({
      id: sport.code,
      name: sport.russianName,
      code: sport.code,
      russianName: sport.russianName,
    }));
    
    // Формируем маппированный объект
    const mappedPlayground = {
      ...playground,
      // Маппим изображения
      images: playground.images || [],
      thumbnail: playground.thumbnail || (playground.images?.[0] ? playground.images[0] : null),
      // Маппим центр
      center: center || (playground.complexId ? { id: playground.complexId } : undefined),
      // Маппим виды спорта
      sports: mappedSports.length > 0 ? mappedSports : (playground.sport ? [{ id: playground.sport, name: `Спорт ${playground.sport}` }] : []),
      // Маппим отзывы (если есть)
      reviews: playground.reviews || playground.comments || [],
      // Маппим количество встреч
      meetingsCount: playground.meetingsCount 
        ? (typeof playground.meetingsCount === 'object' 
            ? Object.values(playground.meetingsCount).reduce((sum: number, count: any) => sum + (count || 0), 0)
            : playground.meetingsCount)
        : 0,
      // Маппим статус времени
      timeStatus: playground.timeStatus || (playground.price ? { type: 'Free' } : undefined),
    };
    
    console.log('✅ Mapped playground:', mappedPlayground);
    return mappedPlayground;
  }
);

const playgroundsSlice = createSlice({
  name: 'playgrounds',
  initialState,
  reducers: {
    setPlaygrounds: (state, action: PayloadAction<Playground[]>) => {
      state.items = action.payload;
    },
    setSelectedPlayground: (state, action: PayloadAction<Playground | null>) => {
      state.selected = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlaygroundsLimited.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlaygroundsLimited.fulfilled, (state, action) => {
        state.isLoading = false;
        // Для главной страницы обновляем только если данных еще нет или их меньше
        if (state.items.length === 0 || state.items.length < action.payload.length) {
          state.items = action.payload;
          state.loadedCount = action.payload.length;
          state.totalCount = action.payload.length;
        }
      })
      .addCase(fetchPlaygroundsLimited.rejected, (state, action) => {
        state.isLoading = false;
        const errorMessage = action.payload as string || action.error.message || '';
        if (errorMessage && !errorMessage.includes('401') && !errorMessage.includes('CORS') && !errorMessage.includes('Network Error')) {
          state.error = errorMessage;
        } else {
          state.error = null;
        }
      })
      .addCase(fetchPlaygrounds.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.loadedCount = 0;
        state.totalCount = 0;
      })
      .addCase(fetchPlaygrounds.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.loadedCount = action.payload.length;
        state.totalCount = action.payload.length;
      })
      .addCase(fetchPlaygrounds.rejected, (state, action) => {
        state.isLoading = false;
        // Не устанавливаем ошибку для 401/CORS, так как это нормально для публичного доступа
        const errorMessage = action.payload as string || action.error.message || '';
        if (errorMessage && !errorMessage.includes('401') && !errorMessage.includes('CORS') && !errorMessage.includes('Network Error')) {
          state.error = errorMessage;
        } else {
          // Если это ошибка авторизации/CORS, просто оставляем пустой список
          state.error = null;
        }
      })
      .addCase(fetchPlayground.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlayground.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selected = action.payload;
      })
      .addCase(fetchPlayground.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Ошибка загрузки площадки';
      });
  },
});

export const { setPlaygrounds, setSelectedPlayground, clearError } =
  playgroundsSlice.actions;
export default playgroundsSlice.reducer;

